import security from "@/main";
import Vue from "vue";
import ImgFullscreen from "@/modules/img/components/main/ImgFullscreen";
import Core from "@/modules/core/service/Core";
import { getApiBaseUrl } from "@/config";
import { pickRendition } from "@/modules/img/components/service/Renditions";
import { MEDIA, combineFolders, tagImages } from "@/modules/img/components/service/Folders";

// Media service (HCIT-1105), queried in addition to the Image Service
const mediaUrl = {
    folders  : "/api/media/sites/{0}/folders",
    upload   : "/api/media/sites/{0}/files",
    uploadState: "/api/media/uploads/{0}",
    images   : "/api/media/sites/{0}/folders/{1}/images",
    rendition: "/api/media/files/{0}/renditions/{1}",
};

// Resolves to {value} or {error}, so one failing service doesn't hide the other's result
const settle = (promise) => promise.then(value => ({ value }), error => ({ error }));

const url = {
    download: {
        init: "/api/image/unit/{0}/export",
        prepare: "/api/image/export/{0}",
        store: "/api/image/export/file/{0}"
    }
};

export default {
    url: {
        folders: "/api/image/folder/{0}",
        images : "/api/image/folder/{0}/images",
        thumb  : "/api/image/{0}/thumb",
        image  : "/api/image/{0}",
        sync   : "/api/image/site/{0}/schedule",
        reset  : "/api/image/site/{0}/reset",
        checkRoot: "/api/image/site/{0}?imageroot={1}",
    },

    download: {
        init (unitId) {
            return Core.method.get(url.download.init, unitId,)
        },

        prepare (tx, image) {
            return Core.method.putVoid(url.download.prepare, image, tx)
        },

        download (txId, success, error) {
            Core.transfer.load(url.download.store.replace("{0}", txId), success, error);
        }
    },

    loadLegacy (siteId) {
        return Core.method.get(this.url.folders, siteId);
    },

    loadMedia (siteId) {
        return Core.method.get(mediaUrl.folders, siteId);
    },

    /**
     * The folders of both services, media first (see Folders.js): {folders, schedule}. A
     * service that fails is skipped as long as the other one answers.
     */
    load (siteId) {
        return Promise.all([this.loadLegacy(siteId), this.loadMedia(siteId)].map(settle)).then(([legacy, media]) => {
            if (legacy.error && media.error) {
                return Promise.reject(legacy.error);
            }

            const legacyData = legacy.value || {};
            const mediaData = media.value || {};
            return { ...legacyData, folders: combineFolders(legacyData.folders, mediaData.folders) };
        });
    },

    /** The images of a folder, tagged with the service they come from. */
    images (siteId, folder) {
        const requests = folder.sources.map(({ source, id }) => {
            const request = source === MEDIA
                ? Core.method.get(mediaUrl.images, siteId, id)
                : Core.method.get(this.url.images, id);
            return settle(request.then(images => tagImages(images, source)));
        });

        return Promise.all(requests).then(results => {
            if (results.every(result => result.error)) {
                return Promise.reject(results[0].error);
            }
            return results.flatMap(result => result.value || []);
        });
    },

    /**
     * The URL of an image for a purpose: "thumb", "view" or "full". [image] is an entry of a folder's
     * images (tagged with its source, media service entries carry renditions) or an Image Service id.
     */
    imageUrl (image, purpose) {
        if (image && image.source === MEDIA) {
            const rendition = pickRendition(image.renditions, purpose);
            return rendition ? getApiBaseUrl() + rendition.url : null;
        }

        const id = image && image.id !== undefined ? image.id : image;
        if (purpose === "thumb") {
            return getApiBaseUrl() + this.url.thumb.replace("{0}", id);
        }
        return getApiBaseUrl() + this.url.image.replace("{0}", id) + (purpose === "full" ? "?nocache=true" : "");
    },

    /**
     * URLs to try, in order, for an image known only by its id (e.g. an inspection image): the Image
     * Service, then the media service's display size and its original (a PDF or an image without
     * variants).
     */
    imageUrlsById (imageId) {
        return [
            getApiBaseUrl() + this.url.image.replace("{0}", imageId),
            ...["display", "original"].map(name =>
                getApiBaseUrl() + mediaUrl.rendition.replace("{0}", imageId).replace("{1}", name))
        ];
    },

    /** Creates a folder in the media service; an existing folder of that name is returned again. */
    createFolder (siteId, name) {
        return Core.method.post(mediaUrl.folders, { name }, siteId);
    },

    /**
     * Uploads one image or PDF into a media service folder. Resolves to the upload id once the media
     * service accepted it (it is processed afterwards, see uploadState); rejects with the response.
     */
    upload (siteId, folderId, file) {
        const body = new FormData();
        body.append("file", file);

        return fetch(getApiBaseUrl() + mediaUrl.upload.replace("{0}", siteId) + "?folderId=" + encodeURIComponent(folderId), {
            method : "POST",
            headers: security.header(),
            body,
        }).then(response => {
            if (!response.ok) {
                return Promise.reject(response);
            }
            return response.json().then(accepted => accepted.uploadId);
        });
    },

    /**
     * State of an upload: {status: IN_PROGRESS | DONE | FAILED, fileId, reason}. The media service
     * doesn't know an upload for the first seconds (404), which counts as IN_PROGRESS.
     */
    uploadState (uploadId) {
        return fetch(getApiBaseUrl() + mediaUrl.uploadState.replace("{0}", uploadId), {
            method : "GET",
            headers: security.jsonHeader(),
        }).then(response => {
            if (response.status === 404) {
                return { status: "IN_PROGRESS" };
            }
            return response.ok ? response.json() : Promise.reject(response);
        });
    },

    check (siteId, root) {
        return Core.method.get(this.url.checkRoot, siteId, root);
    },

    sync (siteId) {
        return Core.method.post(this.url.sync, null, siteId);
    },

    reset (siteId) {
        return Core.method.postVoid(this.url.reset, null, siteId);
    },

    /** [image]: an entry of a folder's images, or an Image Service id. */
    fullscreen (component, image) {
        const header = security.header();
        const handle = window.open("", '_blank', "menubar=no,toolbar=no,location=no,status=no");
        const cmp = new Vue({...ImgFullscreen, });


        component.$nextTick(() => {
            cmp.$mount();
            cmp.$nextTick(() => {
                console.log("full")
                cmp.setToken(header);
                cmp.setImage(image);
                handle.document.body.appendChild (cmp.$el);
            })
        });
    }
}